import type { CSSProperties } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClipboardQuestion, faEye } from "@fortawesome/free-solid-svg-icons";
import { lessonIconDefinition } from "./lessonIcons";
import { textColorsForBackground } from "./lessonCardColors";

export interface QuizPreviewTileProps {
  readonly quizId: string;
  readonly quizTitle: string;
  readonly courseId: string;
  readonly cohortId?: string;
  readonly returnTo?: string;
  readonly iconName?: string | null;
  readonly iconBgColor?: string | null;
  readonly iconFgColor?: string | null;
  readonly size?: "sm" | "md";
}

const DEFAULT_QUIZ_BG = "#1b4d2e";

function quizPreviewUrl(args: {
  courseId: string;
  quizId: string;
  cohortId?: string;
  returnTo?: string;
}): string {
  if (args.cohortId) {
    const base = `/teacher/cohorts/${encodeURIComponent(args.cohortId)}/quizzes/${encodeURIComponent(args.quizId)}/preview`;
    return args.returnTo ? `${base}?returnTo=${encodeURIComponent(args.returnTo)}` : base;
  }
  return `/teacher/courses/${encodeURIComponent(args.courseId)}/quizzes/${encodeURIComponent(args.quizId)}/preview`;
}

/** Compact quiz tile with hover preview, mirroring LessonPreviewTile layout. */
export function QuizPreviewTile({
  quizId,
  quizTitle,
  courseId,
  cohortId,
  returnTo,
  iconName,
  iconBgColor,
  iconFgColor,
  size = "sm",
}: QuizPreviewTileProps) {
  const previewUrl = quizPreviewUrl({ courseId, quizId, cohortId, returnTo });
  const background = iconBgColor?.trim() || DEFAULT_QUIZ_BG;
  const colors = textColorsForBackground(background);
  const watermarkColor = iconFgColor?.trim() || colors.title;
  const icon = lessonIconDefinition(iconName) ?? faClipboardQuestion;

  return (
    <Link
      to={previewUrl}
      className={["lesson-preview-tile", `lesson-preview-tile--${size}`].join(" ")}
      data-tone={colors.tone}
      style={{ "--lesson-preview-bg": background } as CSSProperties}
      title={`Preview ${quizTitle}`}
      aria-label={`Preview quiz ${quizTitle}`}
    >
      <span className="lesson-preview-tile-watermark" style={{ color: watermarkColor }} aria-hidden>
        <FontAwesomeIcon icon={icon} fixedWidth />
      </span>
      <span className="lesson-preview-tile-play" aria-hidden>
        <FontAwesomeIcon icon={faEye} />
      </span>
    </Link>
  );
}
